import { fetchWithAuth, getApiBaseUrl } from './api';
import { ensureOk, okJson } from './httpError';

// iot-connect deliberately answers 502 for these endpoints when the physical MG3000 is
// unreachable (see the guarita exception in api.ts's deploy-banner logic), so the generic
// "service unavailable" wording would be misleading here.
const GUARITA_UNREACHABLE = 'A guarita não respondeu. Verifique se o equipamento está online.';

export interface GuaritaIntegration {
  id: string;
  customer_id: string;
  name: string;
  connection_password: string;
  header_hex: string;
  footer_hex: string;
  access_code: string;
  is_active: boolean;
  vehicle_whitelist_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface GuaritaIntegrationCreate {
  customer_id: string;
  name: string;
  connection_password: string;
  header_hex: string;
  footer_hex: string;
  access_code: string;
  is_active?: boolean;
}

export interface GuaritaIntegrationUpdate {
  name?: string;
  connection_password?: string;
  header_hex?: string;
  footer_hex?: string;
  access_code?: string;
  is_active?: boolean;
  vehicle_whitelist_enabled?: boolean;
}

export interface GuaritaStatus {
  integration_id: string;
  server_running: boolean;
  connected_devices: string[];
  last_event_at?: string;
}

export interface GuaritaDeviceRecord {
  device_type: number;
  device_type_name: string;
  serial_hex: string;
  serial_decimal: string;
  label: string;
  unit: number;
  block_name: string;
  receivers: number;
}

const base = () => getApiBaseUrl();

// CRUD — proxied through condo-manager
export async function listGuaritaIntegrations(customerId: string): Promise<GuaritaIntegration[]> {
  const res = await fetchWithAuth(`${base()}/api/v1/guarita-integrations?customer_id=${customerId}`);
  // Unchecked, an error body fell through `json.data ?? json` and was returned as if it were
  // the integration list, so callers got a non-array and failed later with an unrelated message.
  const json = await okJson<any>(res, 'Erro ao carregar as integrações Guarita');
  return json.data ?? json;
}

export async function createGuaritaIntegration(data: GuaritaIntegrationCreate): Promise<GuaritaIntegration> {
  const { customer_id, ...body } = data;
  const res = await fetchWithAuth(`${base()}/api/v1/guarita-integrations?customer_id=${customer_id}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  await ensureOk(res, 'Erro ao criar a integração Guarita');
  return res.json();
}

export async function updateGuaritaIntegration(id: string, data: GuaritaIntegrationUpdate): Promise<GuaritaIntegration> {
  const res = await fetchWithAuth(`${base()}/api/v1/guarita-integrations/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  await ensureOk(res, 'Erro ao atualizar a integração Guarita');
  return res.json();
}

export async function deleteGuaritaIntegration(id: string): Promise<void> {
  const res = await fetchWithAuth(`${base()}/api/v1/guarita-integrations/${id}`, { method: 'DELETE' });
  // The response was previously discarded, so a failed delete resolved normally and the UI
  // removed a row that still existed on the server.
  await ensureOk(res, 'Erro ao remover a integração Guarita');
}

// Status & commands — proxied through iot-connect
export async function getGuaritaStatus(id: string): Promise<GuaritaStatus> {
  const res = await fetchWithAuth(`${base()}/api/v1/iot-connect/guarita/${id}/status`);
  return okJson(res, GUARITA_UNREACHABLE);
}

export async function guaritaOpenDoor(id: string, door: number = 1, saida: number = 1): Promise<void> {
  const res = await fetchWithAuth(`${base()}/api/v1/iot-connect/guarita/${id}/open-door`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ door, saida }),
  });
  // Door commands must never fail silently — the response was discarded, so an unreachable
  // MG3000 or a rejected command looked exactly like a door that opened.
  await ensureOk(res, GUARITA_UNREACHABLE);
}

export async function reloadGuaritaIntegration(id: string): Promise<void> {
  const res = await fetchWithAuth(`${base()}/api/v1/iot-connect/guarita/${id}/reload`, { method: 'POST' });
  await ensureOk(res, GUARITA_UNREACHABLE);
}

// Device registrations — managed via iot-connect (live TCP connection).
// force=true blocks until a live read of the device completes instead of returning
// whatever's cached from the last background refresh (see ZonesDevices.tsx for the
// same pattern — the cache can be stale by up to one refresh cycle).
export async function listDeviceRegistrations(id: string, force = false): Promise<GuaritaDeviceRecord[]> {
  const res = await fetchWithAuth(`${base()}/api/v1/iot-connect/guarita/${id}/device-registrations${force ? '?force=true' : ''}`);
  // The `json.error` check below only caught errors returned with a 200. A real non-OK status
  // reached res.json() directly, which throws on the HTML body an edge 502 returns.
  const json = await okJson<any>(res, GUARITA_UNREACHABLE);
  if (json.error) throw new Error(json.error);
  return json.data ?? json;
}

export async function registerOnDevice(id: string, cardDecimal: string, label: string, receptor: number = 1, deviceType?: number): Promise<void> {
  const res = await fetchWithAuth(`${base()}/api/v1/iot-connect/guarita/${id}/device-registrations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ card_decimal: cardDecimal, label, receptor, ...(deviceType ? { device_type: deviceType } : {}) }),
  });
  await ensureOk(res, GUARITA_UNREACHABLE);
}

export async function registerOnDeviceWithMask(id: string, cardDecimal: string, label: string, receiversBitmask: number, deviceType?: number): Promise<void> {
  const payload = { card_decimal: cardDecimal, label, receivers_bitmask: receiversBitmask, ...(deviceType ? { device_type: deviceType } : {}) };
  // eslint-disable-next-line no-console
  console.log('[Guarita] POST device-registrations (registerOnDeviceWithMask) →', payload);
  const res = await fetchWithAuth(`${base()}/api/v1/iot-connect/guarita/${id}/device-registrations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  await ensureOk(res, GUARITA_UNREACHABLE);
  // eslint-disable-next-line no-console
  console.log('[Guarita] registerOnDeviceWithMask ← OK', { status: res.status });
}

export async function syncVehiclesToGuarita(id: string): Promise<{ synced: number; errors?: string[] }> {
  const res = await fetchWithAuth(`${base()}/api/v1/iot-connect/guarita/${id}/sync-vehicles`, { method: 'POST' });
  await ensureOk(res, GUARITA_UNREACHABLE);
  return res.json();
}

export async function deleteFromDevice(id: string, cardDecimal: string, deviceType?: number): Promise<void> {
  const qs = deviceType != null ? `?device_type=${deviceType}` : '';
  const url = `${base()}/api/v1/iot-connect/guarita/${id}/device-registrations/${cardDecimal}${qs}`;
  // eslint-disable-next-line no-console
  console.log('[Guarita] DELETE device-registrations →', { url, id, cardDecimal, deviceType });
  const res = await fetchWithAuth(url, {
    method: 'DELETE',
  });
  await ensureOk(res, GUARITA_UNREACHABLE);
  // eslint-disable-next-line no-console
  console.log('[Guarita] deleteFromDevice ← OK', { status: res.status });
}
